import type { TaskNode } from "./types";
import { computeMetrics, progress, treeDoneCounts, type NodeMetrics } from "./tree";
import { ticketUrl } from "./jira";
import { fmt } from "./format";

// A single self-contained read-only HTML file for sharing a plan outside the app —
// nested <details> so the reader can expand/collapse groups, and print-to-PDF friendly
// (everything is force-opened for print). Deliberately standalone and *unlinked*:
//   - no reference to the plan's own URL, guid or storage mode — a reader can't navigate
//     back to the live plan, or discover that it exists, from this file
//   - no external CSS/JS/font/image requests; nothing is fetched when the file is opened
//   - no embedded snapshot: only the human-readable numbers below are written out
// Jira ticket links are the one outbound link kind, and only when the plan configures a
// URL template. Disabled tasks are excluded, like the plain-text report.

export interface HtmlExportCtx {
  projectName: string;
  versions: string[];
  jiraUrl: string; // "{key}" template; empty = ticket keys render as plain badges
  now?: string; // ISO timestamp (injected for tests); default = now
}

function esc(s: string): string {
  return String(s == null ? "" : s)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

const dayLabel = (d: number) => fmt(d) + "d";
const plural = (n: number, one: string) => n + " " + one + (n === 1 ? "" : "s");

// The moment of export, in the *reader's* local time — an ISO/UTC date can name the wrong day
// for anyone west of UTC in the evening. `iso` is injected by tests; an unparseable one falls
// back to now. `hm` is for display; `hmFile` swaps the colon (illegal in a filename) for "-".
function stamp(iso?: string): { date: string; hm: string; hmFile: string } {
  let d = iso ? new Date(iso) : new Date();
  if (isNaN(d.getTime())) d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return {
    date: d.getFullYear() + "-" + p(d.getMonth() + 1) + "-" + p(d.getDate()),
    hm: p(d.getHours()) + ":" + p(d.getMinutes()),
    hmFile: p(d.getHours()) + "-" + p(d.getMinutes()),
  };
}

// The project name as typed, minus the characters no filesystem accepts (and any run of
// whitespace / trailing dots) — a slug would hand the reader "my-plan" instead of "My Plan".
function fileSafeName(name: string): string {
  return (name || "")
    .replace(/[\\/:*?"<>|]/g, "-") // reserved on Windows/macOS; spaces and case are kept
    .replace(/[\u0000-\u001f]/g, "")
    .replace(/\s+/g, " ")
    .replace(/^[.\s]+|[.\s]+$/g, "")
    .slice(0, 90)
    .trim() || "Plan";
}

/** filename for the download: "My Plan [2026-08-11 14-32].html" (local time) */
export function htmlExportFilename(ctx: Pick<HtmlExportCtx, "projectName" | "now">): string {
  const s = stamp(ctx.now);
  return fileSafeName(ctx.projectName) + " [" + s.date + " " + s.hmFile + "].html";
}

// active leaves in document order — the unit both the version summary and the counts use
function activeLeaves(tree: TaskNode[]): TaskNode[] {
  const out: TaskNode[] = [];
  (function walk(list: TaskNode[]) {
    for (const n of list) {
      if (n.disabled) continue;
      if (n.children.length === 0) out.push(n);
      else walk(n.children);
    }
  })(tree);
  return out;
}

// leaf# for every active leaf, 1..N in document order — the same numbering the app's grid shows,
// except disabled leaves are skipped here (they aren't in the export at all, so counting them
// would leave gaps the reader can't explain). Groups are absent from the map → no number.
function leafNumbers(tree: TaskNode[]): Map<string, number> {
  const map = new Map<string, number>();
  activeLeaves(tree).forEach((n, i) => map.set(n.id, i + 1));
  return map;
}

// the badges that trail a task's name: ticket key, version, component, open-question marker
function badges(node: TaskNode, ctx: HtmlExportCtx): string {
  const out: string[] = [];
  if (node.jiraKey) {
    const url = ticketUrl(ctx.jiraUrl, node.jiraKey);
    const key = esc(node.jiraKey);
    out.push(url
      ? `<a class="b b-jira" href="${esc(url)}" target="_blank" rel="noreferrer noopener">${key}</a>`
      : `<span class="b b-jira">${key}</span>`);
  }
  if (node.version) out.push(`<span class="b b-ver">${esc(node.version)}</span>`);
  if (node.component) out.push(`<span class="b b-comp">${esc(node.component)}</span>`);
  if (node.questions) out.push(`<span class="b b-q" title="Open question — needs discussion">❓ TBD</span>`);
  return out.join("");
}

// the right-hand completion cell: "✓" for a finished leaf, "3/7" (or "✓ all") for a group
function stateCell(node: TaskNode, m: NodeMetrics, anyDone: boolean): string {
  if (!anyDone) return `<span class="st"></span>`;
  if (node.children.length === 0) {
    return node.done ? `<span class="st ok">✓</span>` : `<span class="st"></span>`;
  }
  if (!m.totalN) return `<span class="st"></span>`;
  return m.doneN === m.totalN
    ? `<span class="st ok">✓ all</span>`
    : `<span class="st">${m.doneN}/${m.totalN}</span>`;
}

// One task: a group becomes <details> (open unless the plan has it collapsed) whose body holds
// its children; a leaf with a comment becomes <details> too, so the note is expandable the same
// way. Numbering is the app's leaf# — one flat counter over the *active* leaves, groups blank —
// rather than a WBS path: "1.2.3.4" ahead of the name reads as part of it, and the indentation
// already carries the hierarchy here.
function renderNodes(list: TaskNode[], nums: Map<string, number>, depth: number, ctx: HtmlExportCtx, metrics: Map<string, NodeMetrics>, anyDone: boolean, withComments: boolean): string {
  return list.filter((n) => !n.disabled).map((n) => {
    const num = nums.get(n.id);
    const m = metrics.get(n.id) as NodeMetrics;
    const isGroup = n.children.length > 0;
    const comment = withComments ? (n.comment || "").trim() : "";
    const done = isGroup ? (m.totalN > 0 && m.doneN === m.totalN) : n.done;
    const cls = ["row", isGroup ? "grp" : "leaf", done ? "done" : "", depth === 0 ? "top" : ""].filter(Boolean).join(" ");
    const caret = isGroup
      ? `<span class="caret">▸</span>`
      : (comment ? `<span class="caret note-caret">💬</span>` : `<span class="caret flat"></span>`);
    const row = caret
      + `<span class="no">${num == null ? "" : num}</span>`
      + `<span class="nm">${esc(n.name)}</span>`
      + `<span class="bs">${badges(n, ctx)}</span>`
      + `<span class="est">${dayLabel(m.rollup)}</span>`
      + stateCell(n, m, anyDone);
    const note = comment ? `<div class="note">${esc(comment)}</div>` : "";

    if (isGroup) {
      const kids = renderNodes(n.children, nums, depth + 1, ctx, metrics, anyDone, withComments);
      return `<details class="node"${n.collapsed ? "" : " open"}><summary class="${cls}">${row}</summary>`
        + `<div class="kids">${note}${kids}</div></details>`;
    }
    if (comment) {
      return `<details class="node"><summary class="${cls}">${row}</summary><div class="kids">${note}</div></details>`;
    }
    return `<div class="node"><div class="${cls}">${row}</div></div>`;
  }).join("");
}

// A thin progress bar. `pct` is already rounded; a 0-total plan renders an empty track.
function bar(pct: number): string {
  return `<div class="bar"><i style="width:${Math.max(0, Math.min(100, pct))}%"></i></div>`;
}

// Header: name, the totals/counts chips, then overall progress (omitted when nothing is done,
// same rule as the plain-text report) and the open-question count.
function renderHead(tree: TaskNode[], ctx: HtmlExportCtx, anyDone: boolean, generated: string): string {
  const p = progress(tree);
  const leaves = activeLeaves(tree);
  const usedVersions = new Set(leaves.map((n) => n.version).filter(Boolean));
  const verN = ctx.versions.filter((v) => v).length || usedVersions.size;
  let qCount = 0;
  (function count(list: TaskNode[]) {
    list.forEach((n) => { if (n.disabled) return; if (n.questions) qCount++; count(n.children); });
  })(tree);

  const chips = [
    `<span class="chip"><b>${dayLabel(p.total)}</b> total</span>`,
    `<span class="chip"><b>${leaves.length}</b> ${leaves.length === 1 ? "task" : "tasks"}</span>`,
  ];
  if (verN) chips.push(`<span class="chip"><b>${verN}</b> ${verN === 1 ? "version" : "versions"}</span>`);
  if (qCount) chips.push(`<span class="chip warn">❓ ${plural(qCount, "open question")}</span>`);
  chips.push(`<span class="chip dim">Generated ${esc(generated)}</span>`);

  let prog = "";
  if (anyDone) {
    const left = Math.max(0, p.total - p.done);
    prog = `<div class="prog">${bar(p.pct)}`
      + `<div class="prog-note"><b>${p.pct}% done</b> · ${dayLabel(p.done)} of ${dayLabel(p.total)}`
      + (left ? ` · ${dayLabel(left)} left` : "") + `</div></div>`;
  }

  return `<header class="head"><h1>${esc(ctx.projectName || "Untitled project")}</h1>`
    + `<div class="chips">${chips.join("")}</div>${prog}</header>`;
}

// Per-version summary: active leaves grouped by version (configured order, then used-but-
// undefined, then "no version"), each with its task count, day subtotal and % done.
function renderVersions(tree: TaskNode[], ctx: HtmlExportCtx, anyDone: boolean): string {
  const leaves = activeLeaves(tree);
  const order = ctx.versions.filter((v) => v).slice();
  for (const l of leaves) { const v = l.version || ""; if (v && !order.includes(v)) order.push(v); }
  if (!order.length) return ""; // a plan with no versions at all — nothing to summarize

  const rows = order.concat([""]).map((v) => {
    const items = leaves.filter((l) => (l.version || "") === v);
    if (v === "" && items.length === 0) return "";
    const total = items.reduce((s, l) => s + (l.estimate || 0), 0);
    const doneDays = items.reduce((s, l) => s + (l.done ? l.estimate || 0 : 0), 0);
    const pct = total ? Math.round((doneDays / total) * 100) : 0;
    const meta = plural(items.length, "task") + " · " + dayLabel(total) + (anyDone ? " · " + pct + "% done" : "");
    return `<div class="vrow"><span class="b b-ver${v ? "" : " none"}">${esc(v || "— no version")}</span>`
      + `<span class="vmeta">${esc(meta)}</span>${bar(pct)}</div>`;
  }).join("");

  return `<section class="vers"><h2>By version</h2>${rows}</section>`;
}

const STYLE = `
:root {
  --bg: #f4f5f7; --panel: #ffffff; --line: #e2e4e9; --text: #1f2430;
  --muted: #6b7280; --faint: #a6acb8; --accent: #2563eb; --accent-soft: #dbeafe; --bar: #3b82f6;
}
* { box-sizing: border-box; }
body {
  margin: 0; background: var(--bg); color: var(--text); font-size: 13px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
}
.wrap { max-width: 1000px; margin: 0 auto; padding: 22px 16px 40px; }
.head { background: var(--panel); border: 1px solid var(--line); border-radius: 10px; padding: 16px 18px; }
h1 { font-size: 19px; margin: 0 0 10px; font-weight: 650; }
h2 { font-size: 12px; text-transform: uppercase; letter-spacing: 0.06em; color: var(--muted); margin: 0 0 8px; }
.chips { display: flex; flex-wrap: wrap; gap: 6px; }
.chip {
  background: var(--bg); border: 1px solid var(--line); border-radius: 999px;
  padding: 2px 10px; font-size: 11.5px; color: var(--muted);
}
.chip b { color: var(--text); font-weight: 650; }
.chip.warn { background: #fef3c7; border-color: #fde68a; color: #92400e; }
.chip.dim { border-style: dashed; }
.prog { margin-top: 12px; }
.bar { height: 7px; border-radius: 999px; background: var(--accent-soft); overflow: hidden; }
.bar i { display: block; height: 100%; background: var(--bar); border-radius: 999px; }
.prog-note { margin-top: 5px; font-size: 11.5px; color: var(--muted); }
.prog-note b { color: var(--text); }

.tools { display: flex; align-items: center; gap: 7px; margin: 14px 0 8px; }
.tools button {
  font: inherit; font-size: 12px; padding: 4px 11px; border-radius: 7px; cursor: pointer;
  border: 1px solid var(--line); background: var(--panel); color: var(--text);
}
.tools button:hover { border-color: var(--accent); color: var(--accent); }
.tools .spacer { flex: 1; }
.tools .ro { font-size: 11px; color: var(--muted); }

.tree, .vers { background: var(--panel); border: 1px solid var(--line); border-radius: 10px; padding: 8px 12px 12px; }
.vers { margin-top: 14px; padding: 14px 16px 16px; }

.node > .kids { padding-left: 17px; border-left: 1px solid var(--line); margin-left: 7px; }
.row {
  display: flex; align-items: center; gap: 7px; padding: 3px 4px;
  border-radius: 6px; list-style: none; min-height: 24px;
}
summary.row { cursor: pointer; }
summary.row::-webkit-details-marker, summary.row::marker { display: none; content: ""; }
.row:hover { background: #f0f4ff; }
.row.grp { font-weight: 600; }
.row.top { background: #f7f8fa; margin-top: 2px; }
.row.top:hover { background: #f0f4ff; }
.row.done .nm { color: #15803d; }
.caret { flex: none; width: 13px; font-size: 10px; color: var(--muted); transition: transform 0.12s ease; }
details[open] > summary > .caret { transform: rotate(90deg); }
.note-caret, details[open] > summary > .note-caret { transform: none; font-size: 9px; }
/* leaf# gutter: right-aligned and faded well back, so it reads as a reference mark next to the
   task rather than as the first word of its name (blank on group rows). */
.no {
  flex: none; width: 2.4em; text-align: right; color: var(--faint);
  font-variant-numeric: tabular-nums; font-size: 11px; font-weight: 400;
}
.nm { flex: 0 1 auto; min-width: 0; overflow-wrap: anywhere; }
.bs { flex: 0 1 auto; display: flex; flex-wrap: wrap; gap: 3px; }
.est {
  flex: none; margin-left: auto; padding-left: 10px; width: 62px; text-align: right;
  font-variant-numeric: tabular-nums; color: var(--muted);
}
.row.grp .est { color: var(--text); }
.st { flex: none; width: 52px; text-align: right; font-size: 11.5px; color: var(--muted); }
.st.ok { color: #15803d; font-weight: 600; }
.note {
  margin: 2px 0 6px; padding: 7px 10px; border-radius: 7px; background: #f7f8fa;
  border: 1px solid var(--line); color: #384152; font-size: 12px; line-height: 1.55;
  white-space: pre-wrap; overflow-wrap: anywhere; font-weight: 400;
}

.b {
  display: inline-block; font-size: 10.5px; line-height: 1.6; padding: 0 6px;
  border-radius: 999px; border: 1px solid transparent; white-space: nowrap; font-weight: 500;
}
.b-jira { background: var(--accent-soft); color: #1e40af; text-decoration: none; }
a.b-jira:hover { text-decoration: underline; }
.b-ver { background: #ede9fe; color: #5b21b6; border-color: #ddd6fe; }
.b-ver.none { background: var(--bg); color: var(--muted); border-color: var(--line); }
.b-comp { background: #ecfeff; color: #155e75; border-color: #cffafe; }
.b-q { background: #fef3c7; color: #92400e; border-color: #fde68a; }

.vrow { display: grid; grid-template-columns: max-content 1fr 160px; align-items: center; gap: 10px; padding: 5px 0; }
.vmeta { font-size: 11.5px; color: var(--muted); }
.empty { color: var(--muted); padding: 10px 4px; }
footer { margin-top: 16px; font-size: 11px; color: var(--muted); text-align: center; }

@media print {
  body { background: #fff; font-size: 11px; }
  .wrap { max-width: none; padding: 0; }
  .tools { display: none; }
  .head, .tree, .vers { border: none; border-radius: 0; padding: 0; }
  .tree { margin-top: 10px; }
  .vers { margin-top: 14px; break-before: auto; }
  .row:hover { background: none; }
  .row, .note, .vrow { break-inside: avoid; }
  .caret { display: none; }
}
`;

// Expand/Collapse all + print. The beforeprint hook opens every <details> so "Save as PDF"
// captures the whole plan whatever the reader had collapsed, then restores their state.
const SCRIPT = `
(function () {
  var root = document.getElementById('tree');
  if (!root) return;
  var all = function () { return root.querySelectorAll('details'); };
  var setAll = function (open) { Array.prototype.forEach.call(all(), function (d) { d.open = open; }); };
  var on = function (id, fn) { var el = document.getElementById(id); if (el) el.onclick = fn; };
  on('xall', function () { setAll(true); });
  on('call', function () { setAll(false); });
  on('pr', function () { window.print(); });
  var saved = null;
  window.addEventListener('beforeprint', function () {
    saved = [];
    Array.prototype.forEach.call(all(), function (d) { saved.push(d.open); d.open = true; });
  });
  window.addEventListener('afterprint', function () {
    if (!saved) return;
    var i = 0;
    Array.prototype.forEach.call(all(), function (d) { d.open = saved[i++]; });
    saved = null;
  });
})();
`;

/** Build the whole standalone HTML document for a plan. */
export function buildHtmlExport(tree: TaskNode[], ctx: HtmlExportCtx): string {
  const s = stamp(ctx.now);
  const generated = s.date + " " + s.hm; // local, same clock as the filename
  const anyDone = treeDoneCounts(tree).doneN > 0;
  const metrics = computeMetrics(tree);
  const title = ctx.projectName.trim() || "Untitled project";
  const body = renderNodes(tree, leafNumbers(tree), 0, ctx, metrics, anyDone, true);

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<meta name="robots" content="noindex, nofollow">
<title>${esc(title)}</title>
<style>${STYLE}</style>
</head>
<body>
<div class="wrap">
${renderHead(tree, ctx, anyDone, generated)}
<div class="tools">
  <button id="xall" type="button">Expand all</button>
  <button id="call" type="button">Collapse all</button>
  <button id="pr" type="button">Print / Save as PDF</button>
  <span class="spacer"></span>
  <span class="ro">Read-only export</span>
</div>
<section class="tree" id="tree">${body || '<div class="empty">This plan has no active tasks.</div>'}</section>
${renderVersions(tree, ctx, anyDone)}
<footer>${esc(title)} · generated ${esc(generated)} · read-only export</footer>
</div>
<script>${SCRIPT}</script>
</body>
</html>
`;
}
